import { fetchDxApi, fetchDxApiAuthenticated } from "./fetchStudioApi";
import { DXAppBlueprint } from "../../data/DXApp/DXAppBlueprint";
import { getCachedRequest } from "./requestCache";

export interface CreateAppRequest {
    title: string;
    storefront_slug: string;
    app_slug: string;
    description: string;
    theme?: {
        themeSlug: string;
        primaryColor: string;
        secondaryColor: string;
        backgroundColor?: string;
    }
}

export interface AppIdeaRequest {
    appIdea: string;
}

export interface CreateAppResponse {
    app: any;
    landing_page: any;
    onboarding_flow: any;
    pricing: any;
    storefront: any;
    insights_library: any;
    canvas_settings?: {
        canvas_mode?: string;
        landing_page_settings?: {
            view?: string;
            is_view_toggle_visible?: boolean;
        };
        is_mode_bar_visible?: boolean;
        editor_settings?: {
            is_simulator_visible?: boolean;
        };
    };
    generation_complete?: boolean;
}

export interface CreateBrainstormRequest {
    userContext: string;
}

export interface CreateBrainstormResponse {
    brainstorm: any;
    storefront: any;
    canvas_settings?: {
        canvas_mode?: string;
        is_mode_bar_visible?: boolean;
    };
}

export const createApp = async (appData: CreateAppRequest | AppIdeaRequest): Promise<CreateAppResponse> => {
    try {

        // First try with authentication headers if user is logged in
        const response = await fetchDxApiAuthenticated({
            path: '/app-builder/buildApp',
            method: 'POST',
            body: JSON.stringify(appData),
            isJsonPayload: true
        });
        return response.response;
    } catch (error) {
        // If authentication fails or user is not logged in, try with non-auth version
        const response = await fetchDxApi({
            path: '/app-builder/buildApp',
            method: 'POST',
            body: JSON.stringify(appData),
            isJsonPayload: true
        });
        return response.response;
    }
};

export const createBrainstorm = async (brainstormData: CreateBrainstormRequest): Promise<CreateBrainstormResponse> => {
    console.log('[DXAppBuilderApi] Creating brainstorm with data:', brainstormData);

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/app-builder/buildBrainstorm',
            method: 'POST',
            body: JSON.stringify(brainstormData),
            isJsonPayload: true
        });
        console.log('[DXAppBuilderApi] Brainstorm created successfully:', response);
        return response.response;
    } catch (error) {
        console.log('[DXAppBuilderApi] Auth failed, trying non-auth request...');
        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/app-builder/buildBrainstorm',
            method: 'POST',
            body: JSON.stringify(brainstormData),
            isJsonPayload: true
        });
        console.log('[DXAppBuilderApi] Brainstorm created successfully (non-auth):', response);
        return response.response;
    }
};

// New API endpoints for blueprint functionality

export const getCreatorApps = async (withLandingPageInfo: boolean = false, publishedOnly: boolean = false): Promise<any[]> => {
    try {
        // Build query parameters
        const params = new URLSearchParams();
        if (withLandingPageInfo) {
            params.append('with_landing_page_info', 'true');
        }
        if (publishedOnly) {
            params.append('published_only', 'true');
        }

        const queryString = params.toString();
        const path = queryString ? `/app-builder/getCreatorApps?${queryString}` : "/app-builder/getCreatorApps";

        const response = await fetchDxApiAuthenticated({
            path: path,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response.apps || [];
    } catch (error) {
        // Fallback to non-auth request
        try {
            const params = new URLSearchParams();
            if (withLandingPageInfo) {
                params.append('with_landing_page_info', 'true');
            }
            if (publishedOnly) {
                params.append('published_only', 'true');
            }

            const queryString = params.toString();
            const path = queryString ? `/app-builder/getCreatorApps?${queryString}` : "/app-builder/getCreatorApps";

            const response = await fetchDxApi({
                path: path,
                method: 'GET',
                isJsonPayload: false
            });
            return response.response.apps || [];
        } catch (fallbackError) {
            console.error('Error fetching creator apps:', fallbackError);
            return [];
        }
    }
};

// Backwards compatibility alias
export const getUserApps = getCreatorApps;

export const updateRecentlyOpened = async (appId: string): Promise<any> => {
    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/updateRecentlyOpened?appId=${appId}`,
            method: 'POST',
            isJsonPayload: false
        });
        return response.response;
    } catch (error) {
        console.error("Failed to update recently opened timestamp:", error);
        return null;
    }
};

/**
 * Update the isIdea flag for an app (used when converting brainstorm apps to real apps)
 */
export const updateAppIsIdea = async (appId: string, isIdea: boolean): Promise<any> => {
    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/update-app-is-idea`,
            method: 'POST',
            body: JSON.stringify({ appId, isIdea }),
            isJsonPayload: true
        });
        return response.response;
    } catch (error) {
        console.error("Failed to update app isIdea flag:", error);
        throw error;
    }
};

export const getBlueprintForApp = async (appId: string): Promise<any> => {

    console.log("🔍 Calling the blueprint for app in getBlueprintForApp", appId);
    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/getBlueprintForApp?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response;
    } catch (error) {
        // Fallback to non-auth request

        const response = await fetchDxApi({
            path: `/app-builder/getBlueprintForApp?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response;
    }
};

export const updateBlueprint = async (blueprint: any): Promise<any> => {

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/app-builder/updateBlueprint',
            method: 'POST',
            body: JSON.stringify(blueprint),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {
        // Fallback to non-auth request

        const response = await fetchDxApi({
            path: '/app-builder/updateBlueprint',
            method: 'POST',
            body: JSON.stringify(blueprint),
            isJsonPayload: true
        });

        return response.response;
    }
};

export const getSimulatorItemById = async (itemId: string): Promise<any> => {

    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/getSimulatorItemById?itemId=${itemId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/app-builder/getSimulatorItemById?itemId=${itemId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response;
    }
};

export const getMostRecentSimulatorItemByTypeAndAppId = async (appId: string, itemType: string): Promise<any> => {


    // Use cache key to deduplicate requests
    const cacheKey = `simulator-item-${appId}-${itemType}`;

    return getCachedRequest(cacheKey, async () => {
        try {
            const response = await fetchDxApiAuthenticated({
                path: `/app-builder/getMostRecentSimulatorItemByTypeAndAppId?appId=${appId}&itemType=${itemType}`,
                method: 'GET',
                isJsonPayload: false
            });

            return response.response;
        } catch (error) {

            // Fallback to non-auth request
            const response = await fetchDxApi({
                path: `/app-builder/getMostRecentSimulatorItemByTypeAndAppId?appId=${appId}&itemType=${itemType}`,
                method: 'GET',
                isJsonPayload: false
            });

            return response.response;
        }
    });
};

// New function to get all simulator items for an app with pagination
export const getSimulatorItemsForApp = async (
    appId: string,
    page: number = 1,
    limit: number = 10,
    types?: string[]
): Promise<{ items: any[], hasMore: boolean, total: number }> => {


    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('appId', appId);
    queryParams.append('page', String(page));
    queryParams.append('limit', String(limit));

    // Add types filter if provided
    if (types && types.length > 0) {
        queryParams.append('types', types.join(','));
    }

    // Use cache key to deduplicate requests
    const cacheKey = `simulator-items-${appId}-${page}-${limit}-${types?.join(',') || 'all'}`;

    return getCachedRequest(cacheKey, async () => {
        try {
            // Try authenticated request first
            const response = await fetchDxApiAuthenticated({
                path: `/app-builder/getSimulatorItemsForApp?${queryParams.toString()}`,
                method: 'GET',
                isJsonPayload: false
            });


            if (response.response) {
                return response.response;
            }

            return { items: [], hasMore: false, total: 0 };
        } catch (error) {


            try {
                // Fallback to non-auth request
                const response = await fetchDxApi({
                    path: `/app-builder/getSimulatorItemsForApp?${queryParams.toString()}`,
                    method: 'GET',
                    isJsonPayload: false
                });


                if (response.response) {
                    return response.response;
                }

                return { items: [], hasMore: false, total: 0 };
            } catch (error) {

                return { items: [], hasMore: false, total: 0 };
            }
        }
    }, 10000); // Cache for 10 seconds since this is paginated data
};

// New function to save a blueprint example item
export const saveBlueprintExample = async (exampleData: {
    appId: string;
    type: string;
    title: string;
    content: any;
    simId?: string;
}): Promise<any> => {


    try {
        const response = await fetchDxApiAuthenticated({
            path: '/app-builder/saveBlueprintExample',
            method: 'POST',
            body: JSON.stringify(exampleData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/app-builder/saveBlueprintExample',
            method: 'POST',
            body: JSON.stringify(exampleData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to get blueprint examples for an app
export const getBlueprintExamplesForApp = async (appId: string, type?: string): Promise<any[]> => {


    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('appId', appId);
    if (type) {
        queryParams.append('type', type);
    }

    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/getBlueprintExamplesForApp?${queryParams.toString()}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.examples || [];
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/app-builder/getBlueprintExamplesForApp?${queryParams.toString()}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.examples || [];
    }
};

// Function to get a blueprint example by ID
export const getBlueprintExampleById = async (exampleId: string): Promise<any> => {


    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/getBlueprintExampleById?exampleId=${exampleId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.example;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/app-builder/getBlueprintExampleById?exampleId=${exampleId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.example;
    }
};

// Function to update a blueprint example
export const updateBlueprintExample = async (
    exampleId: string,
    content: any,
    title?: string
): Promise<any> => {
    console.log('[DXAppBuilderApi] Updating blueprint example:', { exampleId, content, title });

    const requestData = {
        content,
        title
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/blueprint-examples/${exampleId}`,
            method: 'PATCH',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        console.log('[DXAppBuilderApi] Example updated successfully:', response);
        return response.response;
    } catch (error) {
        console.error('[DXAppBuilderApi] Error updating blueprint example:', error);
        throw error;
    }
};

// Function to regenerate a specific landing page image
export const regenerateLandingPageImage = async (
    appId: string,
    imageType: 'hero' | 'benefits' | 'features' | 'creator',
    specificInstructions?: string
): Promise<any> => {


    const requestData = {
        appId,
        imageType,
        specificInstructions
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/app-builder/regenerateLandingPageImage',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/app-builder/regenerateLandingPageImage',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to get all media items for an app
export const getAppMedia = async (appId: string): Promise<any[]> => {


    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/getAppMedia?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.media || [];
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/app-builder/getAppMedia?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.media || [];
    }
};

// Function to save a media item for an app
export const saveAppMedia = async (
    appId: string,
    type: string,
    url: string
): Promise<any> => {


    const requestData = {
        appId,
        type,
        url
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/app-builder/saveAppMedia',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/app-builder/saveAppMedia',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to get landing page version history for an app
export const getLandingPageVersionHistory = async (appId: string): Promise<any[]> => {


    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/getLandingPageVersionHistory?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.versions || [];
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/app-builder/getLandingPageVersionHistory?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.versions || [];
    }
};

// Function to get blueprint version history for an app
export const getBlueprintVersionHistory = async (appId: string): Promise<any[]> => {


    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/getBlueprintVersionHistory?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.versions || [];
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/app-builder/getBlueprintVersionHistory?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.versions || [];
    }
};

// Function to restore a specific landing page version
export const restoreLandingPageVersion = async (
    appId: string,
    versionId: string
): Promise<any> => {


    const requestData = {
        appId,
        versionId
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/app-builder/restoreLandingPageVersion',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/app-builder/restoreLandingPageVersion',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to restore a specific blueprint version
export const restoreBlueprintVersion = async (
    appId: string,
    versionId: string
): Promise<any> => {


    const requestData = {
        appId,
        versionId
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/app-builder/restoreBlueprintVersion',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/app-builder/restoreBlueprintVersion',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to update app canvas settings
export const updateAppCanvasSettings = async (
    appId: string,
    canvasSettings: any
): Promise<any> => {


    const requestData = {
        appId,
        canvasSettings
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/apps/updateApp',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/apps/updateApp',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to edit landing page content based on user prompt
export const editLandingPage = async (editData: {
    appId: string;
    userPrompt: string;
    blueprint: string;
    creatorId?: string;
}): Promise<any> => {
    console.log(`API call: editLandingPage with data:`, editData);

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/app-builder/edit-landing-page',
            method: 'POST',
            body: JSON.stringify(editData),
            isJsonPayload: true
        });
        console.log("API response from authenticated call:", response);
        return response.response;
    } catch (error) {
        console.log("Authenticated call failed, trying non-auth request:", error);
        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/app-builder/edit-landing-page',
            method: 'POST',
            body: JSON.stringify(editData),
            isJsonPayload: true
        });
        console.log("API response from non-auth call:", response);
        return response.response;
    }
};

export async function getBrainstormApps(brainstormId: string): Promise<any> {
    const response = await fetchDxApiAuthenticated({
        path: `/app-builder/getBrainstormApps?brainstormId=${brainstormId}`,
        method: 'GET',
    });

    if (response.error) {
        throw new Error(response.error);
    }

    return response.response;
}

export async function getBrainstormStatus(brainstormId: string): Promise<any> {
    const response = await fetchDxApiAuthenticated({
        path: `/app-builder/getBrainstormStatus?brainstormId=${brainstormId}`,
        method: 'GET',
        isJsonPayload: false
    });

    return response.response;
}

// Editor Plus Button Suggestions API
export interface EditorContext {
    currentLine: string;
    previousLines: string[];
    nextLines: string[];
    fullDocument: string;
    cursorPosition: number;
}

export interface EditorSuggestionItem {
    id: string;
    title: string;
    icon?: string;
    content?: string;
}

export interface EditorSuggestionSection {
    title: string;
    items: EditorSuggestionItem[];
}

export interface EditorSuggestionsResponse {
    sections: EditorSuggestionSection[];
}

// Function to get editor plus button suggestions
export const getEditorPlusButtonSuggestions = async (
    appId: string,
    context: EditorContext
): Promise<EditorSuggestionsResponse> => {
    console.log(`Getting editor suggestions for app ${appId} with context:`, context);

    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/getEditorPlusButtonSuggestions?appId=${appId}`,
            method: 'POST',
            body: JSON.stringify(context),
            isJsonPayload: true
        });

        console.log('✅ Successfully fetched editor suggestions:', response.response);
        return response.response;
    } catch (error) {
        console.error('❌ Error getting editor suggestions (auth):', error);

        try {
            // Fallback to non-auth request
            const response = await fetchDxApi({
                path: `/app-builder/getEditorPlusButtonSuggestions?appId=${appId}`,
                method: 'POST',
                body: JSON.stringify(context),
                isJsonPayload: true
            });

            console.log('✅ Successfully fetched editor suggestions (non-auth):', response.response);
            return response.response;
        } catch (fallbackError) {
            console.error('❌ Error getting editor suggestions (non-auth):', fallbackError);

            // Return default suggestions as fallback
            return {
                sections: [
                    {
                        title: 'Suggested',
                        items: [
                            { id: 'suggested-1', title: 'Suggested custom example 1', icon: 'lightbulb', content: 'Suggested custom example 1' },
                            { id: 'suggested-2', title: 'Suggested custom example 2', icon: 'lightbulb', content: 'Suggested custom example 2' }
                        ]
                    },
                    {
                        title: 'Stories',
                        items: [
                            { id: 'story-1', title: 'Teach about nutrition', icon: 'menu_book', content: '### Nutrition Story\n\n' },
                            { id: 'story-2', title: 'Explain progressive overload', icon: 'fitness_center', content: '### Progressive Overload\n\n' },
                            { id: 'story-3', title: 'Quiz about nutrition', icon: 'quiz', content: '### Nutrition Quiz\n\n' }
                        ]
                    },
                    {
                        title: 'Activities',
                        items: [
                            { id: 'activity-1', title: 'Long run', icon: 'directions_run', content: '### Long Run Activity\n\n' },
                            { id: 'activity-2', title: 'HIIT workout', icon: 'local_fire_department', content: '### HIIT Workout\n\n' },
                            { id: 'activity-3', title: 'Mobility focused workout', icon: 'self_improvement', content: '### Mobility Workout\n\n' }
                        ]
                    }
                ]
            };
        }
    }
};

// Test Flow API Functions

export interface TestingStep {
    id: string;
    type: 'website' | 'onboarding' | 'day';
    day: number;
    testingInstructions: string;
    status: 'notStarted' | 'completed';
    prompt?: string;
    userDataReport?: string;  // Markdown report of user data for this step
}

export interface DXAppBuilderTestFlow {
    id: string;
    appId: string;
    creatorUserId: string;
    createdAt: string;
    updatedAt: string;
    title: string;
    testingSteps: TestingStep[];
}

// Create a new test flow
export const createTestFlow = async (appId: string, title?: string): Promise<DXAppBuilderTestFlow> => {
    console.log('[DXAppBuilderApi] Creating test flow for app:', appId);

    const requestData = {
        appId,
        title
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/app-builder/createTestFlow',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {
        console.error('[DXAppBuilderApi] Error creating test flow:', error);
        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/app-builder/createTestFlow',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Get all test flows for an app
export const getTestFlows = async (appId: string): Promise<DXAppBuilderTestFlow[]> => {
    console.log('[DXAppBuilderApi] Getting test flows for app:', appId);

    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/getTestFlows?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.testFlows || [];
    } catch (error) {
        console.error('[DXAppBuilderApi] Error getting test flows:', error);
        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/app-builder/getTestFlows?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.testFlows || [];
    }
};

// Update a test flow (including step status updates)
export const updateTestFlow = async (testFlowId: string, updates: Partial<DXAppBuilderTestFlow>): Promise<DXAppBuilderTestFlow> => {
    console.log('[DXAppBuilderApi] Updating test flow:', testFlowId, updates);

    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/updateTestFlow/${testFlowId}`,
            method: 'POST',
            body: JSON.stringify(updates),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {
        console.error('[DXAppBuilderApi] Error updating test flow:', error);
        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/app-builder/updateTestFlow/${testFlowId}`,
            method: 'POST',
            body: JSON.stringify(updates),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Delete a test flow
export const deleteTestFlow = async (testFlowId: string): Promise<{ success: boolean }> => {
    console.log('[DXAppBuilderApi] Deleting test flow:', testFlowId);

    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/deleteTestFlow/${testFlowId}`,
            method: 'DELETE',
            isJsonPayload: false
        });

        return response.response;
    } catch (error) {
        console.error('[DXAppBuilderApi] Error deleting test flow:', error);
        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/app-builder/deleteTestFlow/${testFlowId}`,
            method: 'DELETE',
            isJsonPayload: false
        });

        return response.response;
    }
};

// Get testing-specific onboarding info
export const getTestingOnboardingInfo = async (appId: string, testingStepId: string): Promise<any> => {
    console.log('[DXAppBuilderApi] Getting testing onboarding info:', { appId, testingStepId });

    try {
        const response = await fetchDxApiAuthenticated({
            path: `/app-builder/getTestingOnboardingInfo?appId=${appId}&testingStepId=${testingStepId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response;
    } catch (error) {
        console.error('[DXAppBuilderApi] Error getting testing onboarding info:', error);
        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/app-builder/getTestingOnboardingInfo?appId=${appId}&testingStepId=${testingStepId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response;
    }
};

// Save testing-specific onboarding info
export const saveTestingOnboardingInfo = async (data: {
    appId: string;
    testingStepId: string;
    userState: any;
    pagesInfo?: any;
    isCompleted?: boolean;
}): Promise<any> => {
    console.log('[DXAppBuilderApi] Saving testing onboarding info:', data);

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/app-builder/saveTestingOnboardingInfo',
            method: 'POST',
            body: JSON.stringify(data),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {
        console.error('[DXAppBuilderApi] Error saving testing onboarding info:', error);
        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/app-builder/saveTestingOnboardingInfo',
            method: 'POST',
            body: JSON.stringify(data),
            isJsonPayload: true
        });

        return response.response;
    }
};
